import React, { useEffect, useMemo, useState } from "react";
import { listInvoices, updateInvoice, resendInvoice, deliverInvoice, deleteInvoice, invoicePdfUrl } from "../lib/api";
import { SectionTitle, StatBlock, EmptyState } from "../components/UIKit";
import { Receipt, ArrowClockwise, CheckCircle, MagnifyingGlass, PaperPlaneTilt, Download, Trash, Warning, Clock, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_META = {
  pending:   { label: "PENDING",   color: "bg-white border-black/25 text-[#0A0A0A]",   icon: Clock },
  paid:      { label: "PAID",      color: "bg-[#D4FF00] border-black text-[#0A0A0A]", icon: CheckCircle },
  overdue:   { label: "OVERDUE",   color: "bg-red-100 border-red-600 text-red-800",   icon: Warning },
  cancelled: { label: "CANCELLED", color: "bg-[#F4F4F2] border-black/40 text-[#525252]", icon: XCircle },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso.substring(0, 10); }
}

export default function InvoicesPage() {
  const [data, setData] = useState({ items: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [actioning, setActioning] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (q.trim()) params.q = q.trim();
      const res = await listInvoices(params);
      setData(res);
    } catch { toast.error("Failed to load invoices"); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const markPaidAndDeliver = async (inv) => {
    if (!window.confirm(`Mark ${inv.number} as PAID and auto-deliver the CSV to ${inv.buyer.email}?`)) return;
    setActioning(inv.id);
    try {
      const res = await deliverInvoice(inv.id);
      toast.success(`Delivered ${res.rows} leads to ${inv.buyer.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delivery failed");
    }
    setActioning(null);
  };

  const setStatus = async (inv, next) => {
    setActioning(inv.id);
    try {
      await updateInvoice(inv.id, { status: next });
      toast.success(`Invoice ${inv.number} → ${next}`);
      load();
    } catch (e) { toast.error("Update failed"); }
    setActioning(null);
  };

  const resend = async (inv) => {
    setActioning(inv.id);
    try {
      await resendInvoice(inv.id);
      toast.success(`Resent to ${inv.buyer.email}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Resend failed"); }
    setActioning(null);
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete ${inv.number}? This does not refund or notify the buyer.`)) return;
    setActioning(inv.id);
    try {
      await deleteInvoice(inv.id);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
    setActioning(null);
  };

  const items = data.items || [];
  const stats = data.stats || {};
  const totalRevenue = ((stats.paid_cents || 0) / 100).toFixed(2);
  const outstanding = (((stats.pending_cents || 0) + (stats.overdue_cents || 0)) / 100).toFixed(2);

  return (
    <div className="px-4 sm:px-6 py-8 max-w-7xl">
      <SectionTitle overline="MONETIZE · TRACKING" title="Invoices" />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="invoice-stats">
        <StatBlock label="Revenue" value={`€${totalRevenue}`} hint={`${stats.paid || 0} paid`} testId="stat-revenue" accent />
        <StatBlock label="Outstanding" value={`€${outstanding}`} hint={`${(stats.pending || 0) + (stats.overdue || 0)} unpaid`} testId="stat-outstanding" />
        <StatBlock label="Pending" value={stats.pending ?? 0} hint="Awaiting payment" testId="stat-pending" />
        <StatBlock label="Overdue" value={stats.overdue ?? 0} hint="Past due date" testId="stat-overdue" />
      </div>

      {/* Filters */}
      <div className="border border-black bg-white p-3 mb-6 flex flex-wrap items-center gap-3" data-testid="invoice-filters">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} weight="bold" className="text-[#525252]" />
          <input
            data-testid="invoice-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search buyer, company, email, invoice number…"
            className="flex-1 text-sm outline-none bg-transparent"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          />
        </div>
        <select
          data-testid="invoice-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-black/25 px-2 py-1.5 text-xs bg-white"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          <option value="all">Any status</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button
          data-testid="invoice-refresh"
          onClick={load}
          className="border border-black bg-white px-2.5 py-1.5 text-[10px] tracking-[0.2em] uppercase hover:bg-black/5 flex items-center gap-1.5"
          style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
        >
          <ArrowClockwise size={12} weight="bold" /> REFRESH
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[#525252]" data-testid="invoices-loading">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Receipt size={22} weight="bold" />}
          title="No invoices yet"
          hint="Head to Products and click REQUEST INVOICE on any SKU to generate the first one."
        />
      ) : (
        <div className="border border-black bg-white overflow-x-auto" data-testid="invoices-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black text-left bg-[#F4F4F2]">
                {["#", "Buyer", "Product", "Amount", "Issued", "Due", "Status", "Actions"].map((h) => (
                  <th key={h} className="py-2 px-3 text-[10px] tracking-[0.2em] uppercase text-[#525252]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => {
                const meta = STATUS_META[inv.status] || STATUS_META.pending;
                const StatusIcon = meta.icon;
                const isPending = inv.status === "pending" || inv.status === "overdue";
                return (
                  <tr key={inv.id} className="border-b border-black/10 last:border-b-0" data-testid={`invoice-row-${inv.id}`}>
                    <td className="py-2 px-3 text-xs whitespace-nowrap" style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700 }}>
                      {inv.number}
                    </td>
                    <td className="py-2 px-3">
                      <div className="font-medium truncate max-w-[200px]" title={inv.buyer.name}>
                        {inv.buyer.company || inv.buyer.name}
                      </div>
                      <div className="text-[10px] text-[#525252] truncate max-w-[200px]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        {inv.buyer.email}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-xs">
                      {inv.items?.[0]?.name || "—"}
                      {inv.items?.length > 1 && <span className="text-[#525252]"> +{inv.items.length - 1} more</span>}
                      <div className="text-[10px] text-[#525252] mt-0.5">
                        {inv.country_code && <span>{inv.country_code}</span>}
                        {inv.lead_type && <span>{inv.country_code ? " · " : ""}<b className="uppercase tracking-wider">{inv.lead_type}</b></span>}
                      </div>
                    </td>
                    <td className="py-2 px-3 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      €{(inv.total_cents / 100).toFixed(2)}
                    </td>
                    <td className="py-2 px-3 text-xs whitespace-nowrap">{fmtDate(inv.issue_date)}</td>
                    <td className="py-2 px-3 text-xs whitespace-nowrap">{fmtDate(inv.due_date)}</td>
                    <td className="py-2 px-3">
                      <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[9px] tracking-[0.15em] uppercase ${meta.color}`} style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                        <StatusIcon size={9} weight="bold" /> {meta.label}
                      </span>
                      {inv.delivered_at && (
                        <div className="text-[9px] text-[#525252] mt-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                          delivered {inv.delivered_row_count} rows
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {isPending && (
                          <button
                            data-testid={`invoice-mark-paid-${inv.id}`}
                            onClick={() => markPaidAndDeliver(inv)}
                            disabled={actioning === inv.id}
                            className="bg-[#0047FF] text-white px-2 py-1 text-[10px] tracking-[0.15em] uppercase inline-flex items-center gap-1 hover:bg-[#0038CC] disabled:bg-[#525252]"
                            style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                            title="Mark as paid and auto-deliver CSV"
                          >
                            <CheckCircle size={10} weight="bold" /> MARK PAID
                          </button>
                        )}
                        <div className="flex gap-1">
                          <a
                            data-testid={`invoice-pdf-${inv.id}`}
                            href={invoicePdfUrl(inv.id)}
                            target="_blank"
                            rel="noreferrer"
                            className="border border-black/25 px-1.5 py-1 text-[10px] tracking-[0.15em] uppercase inline-flex items-center gap-1 hover:bg-black/5"
                            style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                            title="Download PDF"
                          >
                            <Download size={10} weight="bold" />
                          </a>
                          {isPending && (
                            <button
                              data-testid={`invoice-resend-${inv.id}`}
                              onClick={() => resend(inv)}
                              disabled={actioning === inv.id}
                              className="border border-black/25 px-1.5 py-1 text-[10px] tracking-[0.15em] uppercase inline-flex items-center gap-1 hover:bg-black/5"
                              style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                              title="Resend invoice email"
                            >
                              <PaperPlaneTilt size={10} weight="bold" />
                            </button>
                          )}
                          {inv.status !== "cancelled" && isPending && (
                            <button
                              data-testid={`invoice-cancel-${inv.id}`}
                              onClick={() => setStatus(inv, "cancelled")}
                              disabled={actioning === inv.id}
                              className="border border-black/25 px-1.5 py-1 text-[10px] tracking-[0.15em] uppercase inline-flex items-center gap-1 hover:bg-black/5"
                              style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                              title="Cancel"
                            >
                              <XCircle size={10} weight="bold" />
                            </button>
                          )}
                          <button
                            data-testid={`invoice-delete-${inv.id}`}
                            onClick={() => remove(inv)}
                            disabled={actioning === inv.id}
                            className="border border-black/25 px-1.5 py-1 text-[10px] tracking-[0.15em] uppercase inline-flex items-center gap-1 hover:bg-red-50"
                            style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                            title="Delete permanently"
                          >
                            <Trash size={10} weight="bold" />
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
